import { useState } from 'react'
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react'
import type { AgentConfigListItem } from '../../../lib/api'
import type { TeamHealth } from './types'

const AGENT_DESCRIPTIONS: Record<string, string> = {
  content_planner: 'Diseña la estructura y angulo del articulo',
  content_writer: 'Redacta el borrador completo',
  content_brand_guard: 'Verifica tono y valores de marca STRTGY',
  disruption_validator: 'Detecta contenido generico o sin opinion propia',
  quantitative_validator: 'Valida rigor y datos cuantitativos',
  alignment_validator: 'Asegura lenguaje human-centric y directo',
  content_critic: 'Califica calidad y decide aprobacion',
  image_prompter: 'Genera prompts de imagen para el contenido',
  visual_validator: 'Valida coherencia visual y estetica',
  visual_brand_guard: 'Verifica identidad visual de marca',
  tavily_researcher: 'Investiga con busqueda web (Tavily)',
  perplexity_researcher: 'Investiga con IA de busqueda (Perplexity)',
  google_researcher: 'Investiga con Google Search',
}

interface TeamBlockProps {
  teamKey: string
  label: string
  icon: LucideIcon
  agents: AgentConfigListItem[]
  health: TeamHealth
  onAgentSelect: (agentId: string) => void
  defaultOpen?: boolean
}

const HEALTH_STYLES: Record<TeamHealth, string> = {
  healthy: 'border-emerald-300 bg-emerald-50',
  warning: 'border-amber-300 bg-amber-50',
  error: 'border-red-300 bg-red-50',
  idle: 'border-slate-200 bg-white',
}

const HEALTH_BADGE: Record<TeamHealth, { label: string; className: string }> = {
  healthy: { label: 'OK', className: 'bg-emerald-100 text-emerald-700' },
  warning: { label: 'Revision', className: 'bg-amber-100 text-amber-700' },
  error: { label: 'Error', className: 'bg-red-100 text-red-700' },
  idle: { label: 'Sin datos', className: 'bg-slate-100 text-slate-500' },
}

export function TeamBlock({
  teamKey: _teamKey,
  label,
  icon: Icon,
  agents,
  health,
  onAgentSelect,
  defaultOpen = false,
}: TeamBlockProps) {
  const [open, setOpen] = useState(defaultOpen)
  const badge = HEALTH_BADGE[health]
  const borderStyle = HEALTH_STYLES[health]

  return (
    <div className={`rounded-xl border-2 ${borderStyle} transition-all duration-200`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
      >
        <div className="p-2 rounded-lg bg-white shadow-sm">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800">{label}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{agents.length} agentes</p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-4 border-t border-inherit">
          <ul className="mt-3 space-y-2">
            {agents.map((agent) => (
              <li
                key={agent.agent_id}
                className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{agent.display_name}</p>
                  <p className="text-xs text-slate-400 truncate">
                    {AGENT_DESCRIPTIONS[agent.agent_id] ?? 'Agente especializado'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onAgentSelect(agent.agent_id)}
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded hover:bg-emerald-50 transition-colors shrink-0"
                >
                  Configurar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
